import React, { useEffect, useMemo, useState } from 'react';
import { EmployeeFinderProps } from './types';
import { FinderEmptyState, List, Wrapper } from './employee-finder.styles';
import { Search } from '@vtbhome/juicyfront';
import { useUsersListSharedQuery } from '@entities/user/libs/hooks/use-users-list-shared-query/use-users-list-shared-query';
import { useCurrentOrganizationPreviewSharedQuery } from '@entities/organization/libs/hooks/use-current-organization-preview-shared-query/use-current-organization-preview-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { debounce } from '@vtbhome/juicyfront/utils/helpers';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { CardUserFinderBuyOrder } from '../card-user-finder-buy-order/card-user-finder-buy-order';
import { UserListItem } from '@shared/model/user';
import { ModalEmployeeListItemMoreInfo } from '@entities/employee/employee-list-item-more-info/ui/modal-employee-list-item-more-info';
import { Typography } from '@shared/ui/typography/typography';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';

export const EmployeeFinder: React.FC<EmployeeFinderProps> = ({ order }) => {
    const currentUser = useCurrentUserSelector();

    const [isShowList, setIsShowList] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [modalUser, setModalUser] = useState<UserListItem | null>(null);

    const [, currentOrganizationResult] = useCurrentOrganizationPreviewSharedQuery(currentUser?.company?.id || 0);
    const currentOrganizationId = currentOrganizationResult.data?.id;

    const usersParameters = useMemo(() => {
        return {
            search: searchValue,
            companies: [String(currentOrganizationId)],
            active: true,
            take: null,
            skip: null,
        };
    }, [searchValue, currentOrganizationId]);

    const [getUsers, getUsersResult] = useUsersListSharedQuery(usersParameters);
    const [isInitGetUsers, setIsInitGetUsers] = useState(false);
    const usersError = getQueryError(getUsersResult);

    const handleChangeSearch = debounce((e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        setSearchValue(target.value);
    }, 200);

    const handleResetFinder = () => {
        setIsShowList(false);
        setSearchValue('');
    };

    const handleClickUserCard = (user: UserListItem) => () => {
        setModalUser(user);
    };

    useEffect(() => {
        (async function () {
            if (usersParameters.search) {
                setIsShowList(true);
                await getUsers(usersParameters);
                setIsInitGetUsers(true);
            } else {
                handleResetFinder();
                setIsInitGetUsers(false);
            }
        })();
    }, [usersParameters]);

    return (
        <Wrapper>
            <Search value={searchValue} onChange={handleChangeSearch} onClear={handleResetFinder} />
            <List>
                {isShowList && (
                    <NetworkStateNew
                        size='s'
                        isLoading={getUsersResult.isFetching || getUsersResult.isLoading}
                        isInitialized={isInitGetUsers}
                        isError={Boolean(usersError)}
                        hasData={Boolean(getUsersResult.data?.items.length)}
                        isFiltered={Boolean(getUsersResult.data?.search)}
                        errorProps={{
                            title: 'Ошибка загрузки пользователей',
                            description: usersError?.message,
                        }}
                        initialLoaderProps={{
                            style: { height: 48 },
                        }}
                        emptyProps={{
                            title: 'Пользователи отсутствуют',
                        }}
                        emptyFilteredProps={{
                            customState: (
                                <FinderEmptyState>
                                    <Typography size='body1_short' color='neutral_50'>
                                        По вашему запросу ничего не найдено
                                    </Typography>
                                </FinderEmptyState>
                            ),
                        }}>
                        {getUsersResult.data?.items.map((user) => (
                            <CardUserFinderBuyOrder
                                key={user.id}
                                user={user}
                                order={order}
                                onAddUser={handleResetFinder}
                                onClick={handleClickUserCard(user)}
                            />
                        ))}
                    </NetworkStateNew>
                )}
            </List>
            <ModalEmployeeListItemMoreInfo
                isShow={Boolean(modalUser?.id)}
                userId={modalUser?.id || 0}
                onClose={() => setModalUser(null)}
            />
        </Wrapper>
    );
};
